import { Accordion, ActionIcon, Group, Loader, Menu, Text, useMantineTheme } from '@mantine/core';
import { IconCheck, IconDots, IconRefresh } from '@tabler/icons-react';

import { useCallback } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { type Opportunity, type WithoutPermissions, type OpportunityAssessment } from '../../../types/apiTypes';
import useEnsisMutation from '../../../hooks/useEnsisMutation';
import { NewProposalMenu } from '../../../components/DropdownMenus';
import { Trash } from '../../../icons';
import OpportunityAssessmentPanel from './OpportunityAssessmentPanel';
import { getBidDecisionString, getScoreString } from '../../../utils/stringUtils';
import { hasPermissionsForObject } from '../../../utils/apiUtils';
interface Props {
  assessment: OpportunityAssessment
  onOpenFastDraft: (opportunity?: WithoutPermissions<Opportunity>) => void
}

const OpportunityAssessmentListItem: React.FC<Props> = (props: Props) => {
  const { assessment, onOpenFastDraft } = props;
  const { organization } = useParams();
  const { other: colorVars, colors } = useMantineTheme();
  const shouldBid = assessment?.bid_decision ?? false;
  const bidDecisionText = getBidDecisionString(shouldBid);
  const navigate = useNavigate();
  const opportunity = assessment.opportunity;
  const deleteOpportunityMutation = useEnsisMutation(`/app/opportunities/${assessment?.opportunity?.uid}/data`, {
    requestType: 'delete',
    queryKeysToInvalidate: ['/app/opportunity-assessments']
  });

  const regenerateAssessmentMutation = useEnsisMutation(`/app/opportunity-assessments/${assessment.uid}/regenerate`, {
    requestType: 'post',
    queryKeysToInvalidate: ['/app/opportunity-assessments'],
    showSuccessMessage: false
  });

  const isProcessing = assessment.processing_status === 'PROCESSING' ||
    assessment.processing_status === 'PREPROCESSING' ||
    regenerateAssessmentMutation.isPending;

  const canDelete = hasPermissionsForObject(assessment, 'delete');

  const handleClickFastDraft = useCallback(() => {
    onOpenFastDraft(opportunity);
  }, [onOpenFastDraft, assessment]);

  const handleClickFullSetup = useCallback(() => {
    navigate(`/org/${organization}/proposals/create-proposal/opportunity/${opportunity.uid}`);
  }, []);
  const handleDeleteAssessment = useCallback(() => {
    deleteOpportunityMutation.mutate({});
  }, [deleteOpportunityMutation.mutate]);
  const handleRenegerateAssessment = useCallback(() => {
    regenerateAssessmentMutation.mutate({});
  }, [regenerateAssessmentMutation.mutate]);

  const assessmentMenu = (
    <Menu position='bottom-end' offset={0}>
      <Menu.Target>
        <ActionIcon variant='subtle' onClick={(e) => { e.stopPropagation(); }}>
          <IconDots color={colorVars.primaryIconColor} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          leftSection={<IconRefresh color={colorVars.primaryIconColor} />}
          onClick={(e) => { handleRenegerateAssessment(); e.stopPropagation(); }}
        >
          Regenerate
        </Menu.Item>
        {
          canDelete && (
            <Menu.Item
              leftSection={<Trash />}
              onClick={(e) => { handleDeleteAssessment(); e.stopPropagation(); }}
            >
              Delete
            </Menu.Item>
          )
        }
      </Menu.Dropdown>
    </Menu >
  );

  const loadingState = <Accordion.Item value={assessment.uid}>
    <Accordion.Control
      disabled
      chevron={<Loader size={24} />}
    >
      <Text fz='sm' fw={500} w='80%'>
        {assessment.opportunity.name}
      </Text>;
    </Accordion.Control>
  </Accordion.Item>;

  const control = (<Group gap={16} justify='space-between'>
    <Text fz='sm' fw={500} truncate flex={1} >
      {assessment.opportunity.name}
    </Text>
    <Group gap={28}>
      <Text fz='sm' fw={500} c={colorVars.secondaryTextColor}>
        {getScoreString(assessment?.score ?? undefined)}
      </Text>
      <Group gap={4} w={52} justify='end' mr={16}>
        {shouldBid && <IconCheck color={colors.green[4]} />}
        <Text fz='sm' fw={500} c={shouldBid ? colors.green[4] : colorVars.secondaryTextColor}>
          {bidDecisionText}
        </Text>
      </Group>
      <Group gap={4}>
        <NewProposalMenu
          onClickFastDraft={handleClickFastDraft}
          useIconForTarget
          onClickFullSetup={handleClickFullSetup}
        />
        {assessmentMenu}
      </Group>
    </Group>
  </Group >);

  if (isProcessing) {
    return loadingState;
  }

  return <Accordion.Item value={assessment.uid}>
    <Accordion.Control
      style={{ display: 'flex', minWidth: 0 }}
    >
      {control}
    </Accordion.Control>
    <Accordion.Panel>
      <OpportunityAssessmentPanel assessment={assessment} />
    </Accordion.Panel>
  </Accordion.Item>;
};

export default OpportunityAssessmentListItem;
